import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '@tytapp/user';
import { SubSink } from 'subsink';
import { AppConfig, Redirection, Shell } from '@tytapp/common';
import { ApiOffer, ApiOfferSet, ApiProduction, ApiUser, ApiVOD, OffersApi, OfferSetsApi, ProductionsApi, ShoutOutsApi } from '@tytapp/api';
import { environment } from 'environments/environment';

@Component({
  selector: 'tyt-confirmed-offers',
  templateUrl: './confirmed-offers.component.html',
  styleUrls: ['./confirmed-offers.component.scss']
})
export class ConfirmedOffersComponent implements OnInit {

  backgroundImage = '/assets/join-path/modern_company_value_bg.jpg';

  private shoutOutsApi = inject(ShoutOutsApi);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private offerSetsApi = inject(OfferSetsApi);
  private productionsApi = inject(ProductionsApi);
  private shell = inject(Shell);

  subsink = new SubSink();
  user : ApiUser;

  goUrl: string;
  goText = 'Go to TYT.com';
  enjoyLegend = '';

  pixelImpression = false;

  get displayedGoText() {
    if (this.shoutOutPref) {
      return `Save and ${this.goText}`;
    }

    return this.goText;
  }

  get isFramed() {
    if (typeof window === 'undefined')
      return false;
    return window.top !== window.self;
  }

  get pixelEndpoint() {
    return `${this.offerSetUUID}:${this.offerUUID}/confirmed`;
  }

  get pixelUrl() {
    return `${environment.urls.api}/membership/offer_sets/${this.pixelEndpoint}/impression`;
  }

  get liveScheduleUrl() {
    return `${environment.urls.app}/live`;
  }

  private offerSetUUID : string;
  private offerUUID : string;

  shoutOutPref: string;
  acceptedOffer: ApiOffer;
  presentedOfferSet: ApiOfferSet;

  thankYouMessage = 'Thank You & Welcome to <span style="color:#EFB42C;">PLUS</span>';

  plusMember = true;

  purchaseType: 'Contribution' | 'Membership';
  purchaseUUID: string;
  contributionGrant: number = 0;

  unitLabel(value: number, singularUnit: string, pluralUnit: string) {
    if (value === 1)
      return singularUnit;

    return pluralUnit;
  }

  ngOnInit() {
    this.goUrl = environment.urls.app;

    this.subsink.add(this.userService.userChanged.subscribe(user => this.user = user));

    this.subsink.add(this.route.queryParamMap.subscribe(async params => {
      this.purchaseType = <any>params.get('pt');
      this.purchaseUUID = params.get('pu');

      if(this.purchaseType === 'Contribution') {
        this.thankYouMessage = 'Thank you for your <span style="color:#EFB42C">contribution!</span>';

        this.contributionGrant = Number(params.get('cg') || '0');
        this.plusMember = this.contributionGrant > 0;
      }
    }));

    this.subsink.add(this.route.paramMap.subscribe(async params => {
      const offerSetUUID = params.get('id');
      const offerSet = await this.offerSetsApi.get(offerSetUUID, false).toPromise();

      this.offerSetUUID = offerSetUUID;
      this.offerUUID = params.get('offerId');

      this.presentedOfferSet = offerSet;
      this.acceptedOffer = offerSet.offers.find(x => x.uuid === this.offerUUID);

      this.embedOfferSnippet();

      this.pixelImpression = typeof window !== 'undefined';

      if(offerSet.featured_media_production_cfid) {
        let production : ApiProduction;

        try {
          production = await this.productionsApi.get(offerSet.featured_media_production_cfid).toPromise();
        } catch (e) {
          console.log('Not able to load the featured media. Reason:');
          console.error(e);
        }

        if (production) {
          for(let episode of production.full_length_vods) {
            (<any><Partial<ApiVOD>> episode).type = 'episodes';
          }

          for(let vod of production.vod_clips) {
            (<any><Partial<ApiVOD>> vod).type = 'clips';
          }

          const vodList = [...production.full_length_vods, ...production.vod_clips];

          for(let vod of vodList) {
            if(vod.id === offerSet.featured_media_vod_cfid && vod.is_premium) {
              this.goText = 'Go to Video';
              this.goUrl = `${environment.urls.app}/watch/${production.id}/${(<any> vod).type}/${vod.id}`;
              this.enjoyLegend = 'Continue to Enjoy the Full <span style="color:#EFB42C">PLUS</span> version of the video you were just watching';
            }
          }
        }
      } else if(offerSet.featured_article_cfid) {
        this.goText = 'Go to Article';
        this.goUrl = `${environment.urls.app}/stories/${offerSet.featured_article_cfid}`;
        this.enjoyLegend = 'Continue to Enjoy the Full <span style="color:#EFB42C">PLUS</span> version of the article you were just reading';
      }

      this.state = 'ready';


    }));
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  state: 'loading' | 'ready' = 'loading';
  loadingMessage = 'Just a moment...';

  async saveAndProceed() {
    this.state = 'loading';

    if (this.shoutOutPref === 'yes') {
      try {
        await this.shoutOutsApi.requestShoutOut({
          purchase_type: this.purchaseType,
          purchase_uuid: this.purchaseUUID,
          allow_shout_out: true
        }).toPromise();
      } catch (e) {
        console.error(`Caught error while requesting shout out:`);
        console.error(e);

        let errorMessage = e.message ?? e.error ?? 'An unknown error has occurred';

        if (e.error === 'not-found') {
          errorMessage = `This feature is not currently available.`;
        } else if (e.error === 'unauthenticated') {
          this.shell.alert(
            `Uh oh`,
            `
              We couldn't submit your shout out request because you've been signed out. Please sign in on TYT.com
              and try again.
            `
          );
          this.state = 'ready';
          return;
        }

        await new Promise(r => setTimeout(r, 1000));
        this.shell.alert(
          `Uh oh`,
          `
            There was a problem requesting your shout out:
            <br/><br/>
            ${errorMessage}
            <br/><br/>
            Please try again. If the issue persists, let us know at support@tytnetwork.com!
          `
        );

        this.state = 'ready';
        return;
      }
    } else if (this.shoutOutPref === 'no') {
      try {
        await this.shoutOutsApi.requestShoutOut({
          purchase_type: this.purchaseType,
          purchase_uuid: this.purchaseUUID,
          allow_shout_out: false
        }).toPromise();
      } catch (e) {
        console.log('There was an error when shoutOutPref was set to no', e);
      }
    }

    // Make sure to use window.open() here because sometimes we are in an embedded
    // offer set (iframe) and we don't want to open the destination URL within that
    // iframe.

    window.open(this.goUrl, "_top");
  }

  @ViewChild('offerSnippet')
  offerSnippetElem: ElementRef;

  async embedOfferSnippet() {
    if (!this.acceptedOffer)
      return;

    if (this.acceptedOffer.confirmation_page_javascript) {
      this.offerSnippetElem.nativeElement.appendChild(
        document.createRange()
          .createContextualFragment(this.acceptedOffer.confirmation_page_javascript)
      );
    }
  }

  get enablePixelDebugger() {
    return this.shell.hasFeatureSync('apps.accounts.enable_pixel_debugger');
  }
}
